import {ChangeDetectorRef, Component, Input, OnChanges} from '@angular/core';
import {NgStyle} from '@angular/common';
import {take} from 'rxjs';
import {ImageGraph, ImageGraphPoint} from '../../../../model/layout.model';
import {MeteoMeasurementsService} from '../../../../service/http/meteo-measurements.service';
import {environment} from '../../../../../environments/environment';

interface GraphPoint {
  value: number;
  error: number;
  weekday: string;
}

/**
 * Tendenz-Liniendiagramm (Temperaturtrend) – handgezeichnetes SVG, ohne Chart-Library.
 * Bildet die meteoeffects-WDRLineGraph-Logik nach: weiße Trendlinie über einem
 * Fehlerband (Wert ± Fehler), editierbare Temperatur-Eingabefelder und Wochentags-Labels.
 * Werte kommen aus der Tendenz-Datei (TX_xx = Maxima, E_TX_xx = Fehlerbereich).
 */
@Component({
  selector: 'app-meteo-line-graph',
  templateUrl: './meteo-line-graph.component.html',
  styleUrls: ['./meteo-line-graph.component.scss'],
  imports: [NgStyle]
})
export class MeteoLineGraphComponent implements OnChanges {

  readonly REF_WIDTH = 1920;
  readonly REF_HEIGHT = 1080;
  readonly VB_WIDTH = 1000;   // internes SVG-Koordinatensystem
  readonly VB_HEIGHT = 300;

  @Input() graph?: ImageGraph;
  @Input() measurementsFile?: string;

  points: GraphPoint[] = [];
  private min = 0;
  private max = 1;

  constructor(private measurementsService: MeteoMeasurementsService,
              private cdr: ChangeDetectorRef) {
  }

  ngOnChanges(): void {
    this.load();
  }

  private load(): void {
    const g = this.graph;
    if (!g) {
      return;
    }
    // Server-seitig aufgelöste Punkte direkt übernehmen (kein Fetch nötig).
    if (g.points && g.points.length > 0) {
      this.points = g.points.map((p, i) => ({
        value: p.value,
        error: p.error,
        weekday: this.weekday((g.weekdayOffsetStart ?? g.startIndex) + i)
      }));
      this.recompute();
      this.cdr.detectChanges();
      return;
    }
    // Ohne Server-Punkte: nur lokal selbst holen. Der Abruf ginge inzwischen zwar über den
    // Rest-Service (`forecast/mos/`), aber dieser Weg läuft beim Seitenaufbau (ngOnChanges) –
    // und die Karten sollen beim Öffnen nichts laden. Die Lokalzeit-Zeile "Tendenz"
    // bekommt ihre Punkte deshalb von der ImageComponent auf "MSWR-MOS laden" (`Image.graph`),
    // und landet oben im Zweig mit den fertigen Punkten.
    if (!environment.enableMeasurementsFallback) {
      return;
    }
    const file = this.measurementsFile;
    if (!file) {
      return;
    }
    this.measurementsService.getMeasurements(file).pipe(take(1)).subscribe({
      next: values => {
        this.points = MeteoLineGraphComponent.punkteAus(g, values, k => this.measurementsService.normalizeKey(k))
          .map((p, i) => ({...p, weekday: this.weekday((g.weekdayOffsetStart ?? g.startIndex) + i)}));
        this.recompute();
        // Ansicht sofort nach dem asynchronen Laden aktualisieren – sonst erscheint das
        // Diagramm erst beim nächsten Ereignis (das "erst beim zweiten Klick"-Symptom).
        this.cdr.detectChanges();
      },
      error: err => console.warn('Tendenz-Daten konnten nicht geladen werden:', err)
    });
  }

  /** Feste Y-Grenzen: min(Werte)-maxFehler .. max(Werte)+maxFehler (wie meteoeffects). */
  private recompute(): void {
    if (this.points.length === 0) {
      this.min = 0;
      this.max = 1;
      return;
    }
    const maxErr = Math.max(...this.points.map(p => p.error), 0);
    this.min = Math.min(...this.points.map(p => p.value)) - maxErr;
    this.max = Math.max(...this.points.map(p => p.value)) + maxErr;
    if (this.min === this.max) {
      this.max = this.min + 1;
    }
  }

  /** Position/Größe/Hintergrund des Diagramm-Containers (proportional, mittelpunktzentriert). */
  containerStyle(): { [key: string]: string } {
    const g = this.graph;
    if (!g) {
      return {};
    }
    return {
      left: (g.x / this.REF_WIDTH * 100) + '%',
      top: (g.y / this.REF_HEIGHT * 100) + '%',
      width: (g.width / this.REF_WIDTH * 100) + '%',
      height: (g.height / this.REF_HEIGHT * 100) + '%',
      'background-color': g.backgroundColor ?? '#bfd72b'
    };
  }

  /** X-Koordinate (Spaltenmitte) eines Punktes im SVG-Raum. */
  private x(i: number): number {
    const n = this.points.length || 1;
    return ((i + 0.5) / n) * this.VB_WIDTH;
  }

  /** Y-Koordinate eines Temperaturwertes im SVG-Raum. */
  private y(value: number): number {
    return (this.max - value) / (this.max - this.min) * this.VB_HEIGHT;
  }

  get linePoints(): string {
    return this.points.map((p, i) => `${this.x(i)},${this.y(p.value)}`).join(' ');
  }

  /** Fehlerband als Polygon: oben Wert+Fehler, zurück unten Wert-Fehler. */
  get bandPoints(): string {
    const top = this.points.map((p, i) => `${this.x(i)},${this.y(p.value + p.error)}`);
    const bottom = this.points.map((p, i) => `${this.x(i)},${this.y(p.value - p.error)}`).reverse();
    return [...top, ...bottom].join(' ');
  }

  get maxLabel(): string {
    return Math.round(this.max) + '°C';
  }

  get minLabel(): string {
    return Math.round(this.min) + '°C';
  }

  lineColor(): string {
    return this.graph?.lineColor ?? '#ffffff';
  }

  bandColor(): string {
    return this.graph?.bandColor ?? '#d2e36b';
  }

  /** Temperatur bearbeitet: Wert übernehmen und Diagramm neu berechnen. */
  onTempInput(index: number, raw: string): void {
    const value = parseFloat((raw ?? '').replace(',', '.'));
    this.points[index].value = isNaN(value) ? 0 : value;
    this.recompute();
  }

  /**
   * Die Punkte eines Graphen aus einer geparsten Tendenz-Datei: `count` Werte ab
   * `startIndex`, gelesen unter `valuePrefix`/`errorPrefix` + zweistelligem Index
   * (`TX_05` … `TX_11`, `E_TX_05` …). Dieselbe Rechnung wie `resolveGraph` im Rest-Service.
   * Statisch, damit die ImageComponent sie für `Image.graph` mitbenutzen kann.
   */
  static punkteAus(g: ImageGraph, values: Map<string, string>,
                   normalizeKey: (key: string) => string): ImageGraphPoint[] {
    const points: ImageGraphPoint[] = [];
    for (let i = 0; i < g.count; i++) {
      const idx = g.startIndex + i;
      points.push({
        value: MeteoLineGraphComponent.num(values.get(normalizeKey(g.valuePrefix + MeteoLineGraphComponent.pad2(idx)))),
        error: MeteoLineGraphComponent.num(values.get(normalizeKey(g.errorPrefix + MeteoLineGraphComponent.pad2(idx))))
      });
    }
    return points;
  }

  private static num(raw: string | undefined): number {
    const v = parseFloat((raw ?? '').replace(',', '.'));
    return isNaN(v) ? 0 : v;
  }

  private static pad2(n: number): string {
    return n < 10 ? '0' + n : '' + n;
  }

  /** Wochentagskürzel heute-1+offset (wie meteoeffects WDRLineGraph.getWeekday). */
  private weekday(offset: number): string {
    const weekdays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    return weekdays[((new Date().getDay() - 1 + offset) % 7 + 7) % 7];
  }
}
